import { ModeEnum } from '@/store/modules/settings/types'
import { Ghm } from '@/store/modules/stay/mco/types'
import { GroupingError, StayStatusEnum } from '@/store/modules/stay/types'

// Wire shape of POST /mco/stays/{id}/groupage-simulation (SimulateGroupageRequestPayload).
// The front only ever requests a single scenario, but the backend's shape (and its response,
// see GroupageSimulationResponseDto below) is scenario-array-based, so we still have to nest it.
export type GroupageSimulationRequestDto = {
  scenarios: {
    rums: {
      rumId: number
      dasReferences: string[]
    }[]
  }[]
}

// Wire shape of one entry of the GroupageSimulationResponse[] array (one per requested scenario).
export type GroupageSimulationResponseDto = {
  ghm: Ghm | null
  ghs: string | null
  value: number
  groupingError: GroupingError | null
  majorRumId: number | null
}

export type GroupageSimulation = {
  ghm: Ghm | null
  ghs: string | null
  value: number
  groupingError: GroupingError | null
}

export function toGroupageSimulation(dto: GroupageSimulationResponseDto): GroupageSimulation {
  return {
    ghm: dto.ghm,
    ghs: dto.ghs,
    value: dto.value,
    groupingError: dto.groupingError,
  }
}

// Which RUMs a DAS selection targets, per BUILD-805: dialyse applies it to every RUM of the stay,
// revalo only to the major (groupant) RUM. Shared by the read-only groupage simulation (BUILD-805)
// and the real additionalCodings payload (BUILD-807) — same targeting rule, two different DTOs.
function resolveTargetRumIds(mode: ModeEnum, rums: { id: number, standardModeMajorRum: boolean }[]): number[] {
  return mode === ModeEnum.DIALYSIS
    ? rums.map((rum) => rum.id)
    : rums.filter((rum) => rum.standardModeMajorRum).map((rum) => rum.id)
}

export function buildGroupageSimulationRequest(
  mode: ModeEnum,
  rums: { id: number, standardModeMajorRum: boolean }[],
  dasReferences: string[]
): GroupageSimulationRequestDto {
  if (dasReferences.length === 0) {
    return { scenarios: [] }
  }

  const targetRumIds = resolveTargetRumIds(mode, rums)

  return { scenarios: [{ rums: targetRumIds.map((rumId) => ({ rumId, dasReferences })) }] }
}

// BUILD-809: one scenario per suggestion (each applied alone, unlike buildGroupageSimulationRequest
// above which combines the whole current selection into a single scenario), sent as a single batch
// request so the aside can show each suggestion card's own simulated amount.
export function buildPerSuggestionGroupageSimulationRequest(
  mode: ModeEnum,
  rums: { id: number, standardModeMajorRum: boolean }[],
  dasReferences: string[]
): GroupageSimulationRequestDto {
  const targetRumIds = resolveTargetRumIds(mode, rums)

  return {
    scenarios: dasReferences.map((dasReference) => ({
      rums: targetRumIds.map((rumId) => ({ rumId, dasReferences: [dasReference] })),
    })),
  }
}

// Wire shape of one entry of the `additionalCodings` array taken by POST /stays/mco/update-status
// (RumAdditionalCodingsPayload, see BUILD-806) — dp/dr/medicalActs are omitted, the front only ever
// sends DAS for now.
export type RumAdditionalCodingsPayloadDto = {
  rumId: number
  das: string[]
}

export function buildAdditionalCodingsPayload(
  mode: ModeEnum,
  rums: { id: number, standardModeMajorRum: boolean }[],
  dasReferences: string[]
): RumAdditionalCodingsPayloadDto[] {
  if (dasReferences.length === 0) {
    return []
  }

  return resolveTargetRumIds(mode, rums).map((rumId) => ({ rumId, das: dasReferences }))
}

// Wire shape of the fields we need from the stay entity (stay_full serialization group) returned by
// POST /stays/mco/update-status, to patch the aside and the stay-list row without a full refetch.
export type UpdateStayStatusResponseDto = {
  status: StayStatusEnum
  standardModeGhm: Ghm | null
  standardModeGhs: string | null
  standardModeValue: number
  standardModeGroupingError: GroupingError | null
  firstStandardModeValue: number
  validationDate: string | null
}
